import { useEffect, useState } from "react";
import { Button, Modal, ModalBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import PhotoDropzone from "./PhotoDropzone";
import { createAdminCompanyDeviceIssue, createAdminCompanyDeviceUpdate } from "../../slices/admin/thunk";
import { createSelector } from "@reduxjs/toolkit";

const selectAllDevices = createSelector(
    state => state.Admin.device,
    (deviceObj) => {
        return Object.values(deviceObj)
            .flat()
            .sort((a, b) => a.id - b.id);
    }
);

const AdminDeviceRecordModal = ({ show, onCloseClick, data, fetchData, isIssueUpdate, issueID }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [isShowError, setShowError] = useState(false);

    const devices = useSelector(selectAllDevices);
    const [photos, setPhotos] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [deviceRecordData, setDeviceRecordData] = useState({
        id: '',
        issueID: undefined,
        admin_device_id: '',
        info: ''
    });

    useEffect(() => {
        if (show) {
            const defaultDeviceRecordData = {
                id: '',
                issueID: undefined,
                admin_device_id: devices.length > 0 ? devices[0].id : '',
                info: ''
            };
            setDeviceRecordData({
                ...defaultDeviceRecordData,
                ...(data ?? {}),
                issueID: isIssueUpdate ? issueID : undefined
            });
        }
    }, [show, data, isIssueUpdate, issueID, devices]);

    const closeModal = () => {
        setShowError(false);
        onCloseClick(deviceRecordData);
    };

    // Safe input handler with fallbacks
    const handleChange = (field) => (e) => {
        let value = e.target.value || '';
        setDeviceRecordData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Modal isOpen={show} toggle={() => { closeModal(false) }} centered={true} size="lg">
            <div className="modal-header">
                {isIssueUpdate? issueID===undefined ?
                    <div className="modal-title">{t('PageAdmin.Btn.CreateDeviceIssue')}</div> :
                    <div className="modal-title">{t('PageAdmin.Btn.CreateDeviceIssueUpdate')}</div>:
                <div className="modal-title">{t('PageAdmin.Btn.CreateDeviceUpdate')}</div>}
                <div className="d-flex flex-row modal-close-btn">
                    <Button type="button" className="btn-close btn-sm btn-light" onClick={() => { closeModal(false); }} ></Button>
                </div>
            </div>
            <ModalBody className="d-flex flex-column">
                <form>
                    {issueID===undefined?null:
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="admin-device-record-company">{t('PageAdmin.CompanyDeviceRecordTable.IssueNo')}</label>
                        </div>
                        <div className="col-md-9">
                            {issueID}
                        </div>
                    </div>}
                    {issueID!==undefined?null:
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="admin-device-record-company">{t('PageAdmin.CompanyDeviceRecordTable.DeviceID')}</label>
                        </div>
                        <div className="col-md-9">
                            <select
                                className="form-control"
                                id="admin-record-device-company"
                                value={deviceRecordData.admin_device_id}
                                onChange={handleChange("admin_device_id")}
                            >
                                {
                                    devices.map(device => (
                                        <option key={device.id} value={device.id}>{device.id}</option>
                                    ))
                                }
                            </select>
                        </div>
                    </div>}
                    <div className="row">
                        <div className="col-md-3">
                            <label className="col-form-label" htmlFor="admin-device-record-info">{t('PageAdmin.CompanyDeviceRecordTable.Info')}</label>
                        </div>
                        <div className="col-md-9">
                            <textarea
                                rows={3}
                                maxLength={500}
                                className="form-control"
                                id="admin-device-record-info"
                                value={deviceRecordData.info || ''}
                                onChange={handleChange("info")}
                                style={{ resize: 'vertical', overflowY: 'auto' }}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-3">{t('PageAdmin.CompanyDeviceRecordTable.UploadYourPhotos')}</div>
                        <div className="col-md-9">
                            <PhotoDropzone onFilesSelected={(photos) => { setPhotos(photos) }} maxFiles={8} />
                        </div>
                    </div>
                </form>
                <div className="d-flex flex-column align-items-center company-save-modal-btn-container">
                    {isShowError ? <div className="company-save-modal-error">{t('PageAdmin.SaveCompanyModal.SaveError')}</div> : null}
                    <div className="d-flex flex-row justify-content-center ">
                        <Button className="btn-sm report-save-modal-btn" color="primary" onClick={() => {
                            closeModal(false)
                        }}> {t('PageReport.SaveReportModal.BtnCancel')} </Button>
                        <Button className="btn-sm report-save-modal-btn" color="primary" disabled={isSubmitting} onClick={async () => {   
                            setIsSubmitting(true);                         
                            const formData = new FormData();
                            formData.append('data', JSON.stringify(deviceRecordData));
                            console.log("Submitting formData:", deviceRecordData);
                            photos.forEach((file, idx) => {
                                formData.append('photos', file); // 'photos' is an array field on the backend
                            });
                            let result = false;
                            if (isIssueUpdate) {
                                if (issueID===undefined) {
                                    result = await dispatch(createAdminCompanyDeviceIssue(formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                    }));
                                }
                                else {
                                    result = await dispatch(createAdminCompanyDeviceUpdate(formData, {
                                        headers: { 'Content-Type': 'multipart/form-data' }
                                    }));
                                }
                            }
                            else {
                                result = await dispatch(createAdminCompanyDeviceUpdate(formData, {
                                    headers: { 'Content-Type': 'multipart/form-data' }
                                }));
                            }
                            if (result === true) {
                                fetchData();
                                setShowError(false);
                                closeModal(true);
                            } else {
                                setShowError(true);
                            }
                            setIsSubmitting(false);
                        }}> {t('PageReport.SaveReportModal.BtnSave')} </Button>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    );
}

export default AdminDeviceRecordModal;